import { type InitResult } from '@airwallex/components-sdk'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { type ReactNode, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'

import { useConfig } from '@/providers/config'
import { useConnectedAccount } from '@/providers/connectedAccount'
import { useUserContext } from '@/providers/user/useUserContext'
import { initializeSCASDK } from '@/utils/initializeSCASDK'
import { SCACancelledError } from '@/utils/scaErrors'
import { testAwareMs } from '@/utils/testAwareMs'

import { SCAContext } from './SCAContext'
import type { SCARetryHandler } from './types'

type SCAProviderProps = {
  children: ReactNode
}

/**
 * Upper bound on a single `sdk.sca.getScaToken()` call. The Airwallex SDK is a page-global
 * singleton, and the call has been observed never to settle after that singleton is
 * re-initialized for a different connected account. An unbounded await there wedges every
 * queued request with no modal and no error, so the whole page renders nothing.
 */
const SCA_TOKEN_TIMEOUT_MS = testAwareMs(10_000)

export function SCAProvider({ children }: SCAProviderProps) {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()
  const { activeConnectedAccount } = useConnectedAccount()
  const { config } = useConfig()

  const vesselId = activeConnectedAccount?.vesselId

  const [isOpen, setIsOpen] = useState(false)
  const [scaSessionCode, setScaSessionCode] = useState<string | null>(null)
  const queuedHandlersRef = useRef<SCARetryHandler[]>([])
  const startingRef = useRef(false)
  const cancelGenerationRef = useRef(0)

  // SDK instance management
  const sdkInitResultRef = useRef<InitResult | null>(null)
  const initializingSdkRef = useRef<boolean>(false)
  // Identity the current or in-flight SDK init is for. Stamped before the first await,
  // deliberately: the single-flight guard depends on a concurrent caller seeing this key
  // already claimed. It stays stamped after a failed init, which is harmless —
  // sdkInitResultRef is null, so the next call re-inits.
  const sdkKeyRef = useRef<string | null>(null)

  // One in-flight SDK token retrieval per account. See getSCAToken for why.
  const inFlightTokenRef = useRef<Map<string, Promise<string | null>>>(new Map())

  const sdkKey =
    activeOrganizationId && vesselId && activeConnectedAccountId
      ? `${activeOrganizationId}:${vesselId}:${activeConnectedAccountId}`
      : null

  /**
   * Ensures the SDK is initialized and returns the SDK instance.
   * This is shared between token retrieval and the modal.
   */
  const ensureSDKInitialized = async (): Promise<InitResult | null> => {
    if (!sdkKey) {
      return null
    }

    // Org, vessel or connected account moved. The cached instance is scoped to the
    // previous account, and so is any token it would hand back — discard it.
    if (sdkKeyRef.current !== sdkKey) {
      sdkInitResultRef.current = null

      // Never clear another caller's in-flight flag. init() replaces the page-global
      // Airwallex singleton wholesale, so starting a second init() while one is still
      // running corrupts it. Bail instead: the caller falls back to the modal, and the
      // next request initializes cleanly once the in-flight one has settled.
      if (initializingSdkRef.current) {
        console.warn('SCA SDK init already in flight for a different account; deferring')
        return null
      }
    }

    // Already initialized for this account
    if (sdkInitResultRef.current) {
      return sdkInitResultRef.current
    }

    // Already initializing, wait for it
    if (initializingSdkRef.current) {
      // Wait for initialization to complete (simple polling, could be improved)
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!initializingSdkRef.current && sdkInitResultRef.current) {
            clearInterval(checkInterval)
            resolve(sdkInitResultRef.current)
          } else if (!initializingSdkRef.current) {
            clearInterval(checkInterval)
            resolve(null)
          }
        }, testAwareMs(100))
        setTimeout(() => {
          clearInterval(checkInterval)
          resolve(null)
        }, testAwareMs(5000))
      })
    }

    // Need to initialize
    if (!activeOrganizationId || !activeConnectedAccountId || !vesselId || !config?.awx.clientId) {
      return null
    }

    initializingSdkRef.current = true
    sdkKeyRef.current = sdkKey
    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        return null
      }

      const initResult = await initializeSCASDK({
        orgId: activeOrganizationId,
        vesselId,
        connectedAccountId: activeConnectedAccountId,
        accessToken,
        config: {
          clientId: config.awx.clientId,
          env: config.awx.env as 'dev' | 'staging' | 'demo' | 'prod',
        },
      })

      // The account switched again while this init was in flight. sdkKeyRef now
      // belongs to a newer init; writing our (stale-account) result into
      // sdkInitResultRef would let the newer init's caller mistake it for its own
      // and ask a previous account's SDK for this account's token.
      if (sdkKeyRef.current !== sdkKey) {
        return null
      }

      sdkInitResultRef.current = initResult
      return initResult
    } catch (error) {
      console.error('Failed to initialize SDK:', error)
      return null
    } finally {
      // Only clear the flag if it's still ours — a newer in-flight init for a
      // different account may have taken over initializingSdkRef in the meantime,
      // and clearing it here would let a concurrent caller resolve too early.
      if (sdkKeyRef.current === sdkKey) {
        initializingSdkRef.current = false
      }
    }
  }

  const retrieveSCAToken = async (accountId: string): Promise<string | null> => {
    const sdk = await ensureSDKInitialized()

    if (!sdk?.sca) {
      return null
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined
    try {
      const timeout = new Promise<null>((resolve) => {
        timeoutId = setTimeout(() => {
          console.warn(`SCA token retrieval timed out for connected account ${accountId}`)
          resolve(null)
        }, SCA_TOKEN_TIMEOUT_MS)
      })

      const token = await Promise.race([sdk.sca.getScaToken(), timeout])

      if (token) {
        return token
      }
    } catch (error) {
      // Logged at warn, not debug: this swallow previously hid the account-scoping bug.
      console.warn(`SCA silent refresh failed for connected account ${accountId}:`, error)
    } finally {
      clearTimeout(timeoutId)
    }

    return null
  }

  /**
   * Asks the SDK for an SCA token for the active connected account.
   *
   * We hold no token state of our own: Airwallex stores the SCA token and its refresh
   * token per account in localStorage, so `getScaToken()` is the single source of truth.
   * It returns the stored token while it is live, silently refreshes when it is not, and
   * yields nothing when the user must verify — in which case we return null and the
   * caller opens the modal.
   *
   * @see https://www.airwallex.com/docs/banking-as-a-service__strong-customer-authentication-(sca)__sca-for-transaction-data-retrieval
   */
  const getSCAToken = async (): Promise<string | null> => {
    const accountId = activeConnectedAccountId
    if (!accountId) {
      return null
    }

    // Airwallex rotates the refresh token on every exchange, so two concurrent
    // getScaToken() calls race to rotate the same stored token and the loser is left
    // holding an invalidated one. A page load fires several protected queries at once, so
    // share a single retrieval per account rather than letting them all call the SDK.
    const inFlight = inFlightTokenRef.current.get(accountId)
    if (inFlight) {
      return inFlight
    }

    const request = retrieveSCAToken(accountId)
    inFlightTokenRef.current.set(accountId, request)
    void request.finally(() => {
      if (inFlightTokenRef.current.get(accountId) === request) {
        inFlightTokenRef.current.delete(accountId)
      }
    })

    return request
  }

  const getSCAFlowGeneration = () => cancelGenerationRef.current

  const resetSCAFlow = () => {
    setIsOpen(false)
    setScaSessionCode(null)
    startingRef.current = false
  }

  // Single-flight guard so only one modal is opened per SCA flow
  const isFlowActive = useMemo(() => isOpen && !!scaSessionCode, [isOpen, scaSessionCode])

  const requestSCA = (sessionCode: string, handler: SCARetryHandler, flowGeneration: number) => {
    if (flowGeneration < cancelGenerationRef.current) {
      handler.onCancel(new SCACancelledError())
      return
    }

    queuedHandlersRef.current.push(handler)

    if (isFlowActive || startingRef.current) {
      return
    }

    // A modal flow always starts from a fresh SCA init(): another component may have
    // re-inited the page-global SDK with a non-SCA auth code since we cached ours.
    // Leave initializingSdkRef alone: an in-flight init is newer than the cache, and
    // clearing the flag would let a concurrent caller start a second init(). ISSUE-3746
    sdkInitResultRef.current = null

    startingRef.current = true
    setScaSessionCode(sessionCode)
    setIsOpen(true)
  }

  const handleSCASuccess = async (scaToken: string) => {
    try {
      // Deliberately not cached: a modal-obtained token is a one-time credential bound
      // to the specific funds-transfer request that triggered it. Caching it would let a
      // later, unrelated request replay a dead credential.
      const handlers = queuedHandlersRef.current
      queuedHandlersRef.current = []

      // Resolve all queued requests with the same token
      await Promise.allSettled(handlers.map((h) => h.onToken(scaToken)))
      toast.success('Authentication successful!')
    } catch (error) {
      console.error('Error retrying request after SCA:', error)
      toast.error('Failed to complete the operation. Please try again.')
    } finally {
      resetSCAFlow()
    }
  }

  const handleSCAError = (error: Error) => {
    console.error('SCA verification failed:', error)
    toast.error('Authentication failed. Please try again.')
    // Reject all queued requests
    const handlers = queuedHandlersRef.current
    queuedHandlersRef.current = []
    handlers.forEach((h) => h.onCancel(error))
    resetSCAFlow()
  }

  const handleSCACancel = () => {
    cancelGenerationRef.current += 1
    const handlers = queuedHandlersRef.current
    queuedHandlersRef.current = []
    handlers.forEach((handler) => handler.onCancel(new SCACancelledError()))
    if (handlers.length > 0) {
      toast.error('Authentication has been cancelled. Protected information is unavailable.', { id: 'sca-cancelled' })
    }
    resetSCAFlow()
  }

  const contextValue = {
    // Modal state
    isOpen,
    scaSessionCode,

    // Token management
    getSCAToken,

    // Modal control
    requestSCA,
    getSCAFlowGeneration,
    handleSCASuccess,
    handleSCAError,
    handleSCACancel,

    // SDK instance
    ensureSDKInitialized,
  }

  return <SCAContext.Provider value={contextValue}>{children}</SCAContext.Provider>
}
